import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Onfido } from "onfido-sdk-ui";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  adhocKycErrorMessage,
  useAdhocKycBiometricConsentMutation,
  useAdhocKycOnfidoCompleteMutation,
  useLazyGetAdhocKycOnfidoTokenQuery,
} from "src/store/features/api/adhocKyc";
import { AdhocKycBody, AdhocKycError, AdhocKycHeading, AdhocKycSpinner } from "./AdhocKycShell";
import { BiometricConsentWording } from "./consentCopy";

export interface OnfidoStepProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
}

/**
 * CR2 — shown in front of the SDK until the row holds the biometric consent
 * artefact. "Consent and continue" is the consent act (compliance wording
 * v1.1, no checkbox); posting it refreshes the context
 * (`biometricConsentRecorded` flips), after which `OnfidoStep` asks for the
 * SDK token. The backend refuses a token without it, so this screen cannot
 * be skipped client-side.
 */
export function BiometricConsentStep({ auth, context }: OnfidoStepProps): JSX.Element {
  const [submit, { isLoading, error }] = useAdhocKycBiometricConsentMutation();
  const wordingVersion = String(context.biometricConsentWordingVersion);

  return (
    <Box data-testid="adhoc-kyc-biometric-consent-step">
      <AdhocKycHeading>Your biometric information and your consent</AdhocKycHeading>
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          p: 2,
          mb: 2,
          bgcolor: "#FFFFFF",
        }}
      >
        <BiometricConsentWording />
      </Box>
      {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
      <NextButton
        fullWidth
        disabled={isLoading}
        onClick={() => submit({ auth, body: { wordingVersion, accepted: true } })}
        data-testid="adhoc-kyc-biometric-consent-continue"
      >
        Consent and continue
      </NextButton>
    </Box>
  );
}

export const ONFIDO_MOUNT_ID = "onfido-mount";

/**
 * Step 3 (stale branch only). Mounts the Onfido Studio SDK the way the public
 * `SecureBiometrics` page does — token + workflowRunId from our backend, no
 * session-dependent hooks. `onComplete` posts the run id back and the router
 * re-derives the next screen from the refreshed context; `onError` offers a
 * retry that re-fetches the token (the backend reuses the applicant/run).
 */
export default function OnfidoStep({ auth, context }: OnfidoStepProps): JSX.Element {
  const [fetchToken, { data, isFetching, error: tokenError }] =
    useLazyGetAdhocKycOnfidoTokenQuery();
  const [complete, { isLoading: completing, error: completeError }] =
    useAdhocKycOnfidoCompleteMutation();
  const [sdkError, setSdkError] = useState(false);
  // The SDK can fire onComplete more than once; the second post would 409 on a row that
  // has already moved on and flash an error over a finished step (seen 14 Oct).
  const completedRun = useRef<string | null>(null);
  const token = auth.token;
  const consented = context.biometricConsentRecorded === true;

  useEffect(() => {
    if (consented) fetchToken({ token });
  }, [consented, fetchToken, token]);

  const ready = Boolean(data) && !isFetching && !sdkError && !completing && !completeError;

  useEffect(() => {
    if (!ready) return undefined;
    const run = data as { token: string; workflowRunId: string };
    const handle = Onfido.init({
      token: run.token,
      workflowRunId: run.workflowRunId,
      containerId: ONFIDO_MOUNT_ID,
      language: { locale: "en_US" },
      onComplete: () => {
        if (completedRun.current === run.workflowRunId) return;
        completedRun.current = run.workflowRunId;
        complete({ auth: { token }, body: { workflowRunId: run.workflowRunId } });
      },
      onError: () => setSdkError(true),
    });
    return () => {
      void handle.tearDown();
    };
    // `auth` is reduced to its token so a new object each render doesn't remount the SDK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, data, token]);

  const retry = (): void => {
    setSdkError(false);
    fetchToken({ token });
  };

  if (!consented) return <BiometricConsentStep auth={auth} context={context} />;
  if (isFetching) return <AdhocKycSpinner message="Preparing your ID check" />;
  if (completing) return <AdhocKycSpinner message="Saving your ID check" />;

  return (
    <Box data-testid="adhoc-kyc-onfido-step">
      <AdhocKycHeading>Quick ID check</AdhocKycHeading>
      <AdhocKycBody>
        Because your details have changed we need to confirm it&rsquo;s you. Have your photo ID
        ready — this takes about a minute.
      </AdhocKycBody>
      {tokenError ? (
        <>
          <AdhocKycError message={adhocKycErrorMessage(tokenError)} />
          <BackButton fullWidth onClick={retry} data-testid="adhoc-kyc-onfido-retry">
            Try again
          </BackButton>
        </>
      ) : null}
      {sdkError ? (
        <>
          <AdhocKycError message="The ID check couldn't start. Please try again." />
          <BackButton fullWidth onClick={retry} data-testid="adhoc-kyc-onfido-retry">
            Try again
          </BackButton>
        </>
      ) : null}
      {completeError ? (
        <>
          <AdhocKycError message={adhocKycErrorMessage(completeError)} />
          <BackButton
            fullWidth
            onClick={() =>
              complete({
                auth: { token },
                body: { workflowRunId: (data as { workflowRunId: string }).workflowRunId },
              })
            }
            data-testid="adhoc-kyc-onfido-complete-retry"
          >
            Try again
          </BackButton>
        </>
      ) : null}
      <Box id={ONFIDO_MOUNT_ID} data-testid="adhoc-kyc-onfido-mount" sx={{ minHeight: 480 }} />
    </Box>
  );
}
